/* TODO: Remove the linting omission from the workflows
(This is the PR: https://github.com/ApplyBoard/platform_monorepo_ui/pull/7178) */
import { addDays, addMonths, addYears, startOfDay } from 'date-fns';
import { TFunction } from 'i18next';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { mapValues } from 'lodash';
// eslint-disable-next-line no-restricted-imports
import { useEffect, useState } from 'react';
import { ZodError } from 'zod';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Combobox,
  ComboboxOptionProps,
  DatePicker,
  FileUpload,
  Flex,
  GridAlpha,
  Link,
  Notice,
  NumberInput,
  PhoneInput,
  RadioGroup,
  Select,
  Text,
  TextInput,
  Tooltip,
  createForm,
  useId,
  useToasts,
} from '@applyboard/crystal-ui';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import { RbcLogo } from '../../assets/RbcLogo';
import TextInputWithFilter from '../../common/components/TextInputWithFilter';
import { FULL_COUNTRY_OPTIONS_LIST } from '../../common/constants';
import { VasError, VasErrorBody } from '../../common/errors/VasError';
import { useGetTrackingEventData } from '../../common/hooks/useQueryParams';
import { zodValidateField } from '../../common/schema/forms';
import { htmlDecode } from '../../common/utils/decodeHtml';
import { validateSupportedMimeTypes } from '../../common/utils/validateSupportedMimeTypes';
import {
  CreateRbcGicApplicationReqV2Body,
  CreateRbcGicApplicationResV2Body,
  formDataToCreateV2Body,
  useCreateRbcGicApplicationV2,
} from '../../data/rbcGic';
import {
  COLUMN_SETTING,
  COUNTRIES_REQUIRE_PROVINCES,
  EMPLOYMENT_STATUS_EMPLOYED,
  EMPLOYMENT_STATUS_LIST,
  EMPLOYMENT_STATUS_STUDENT_NOT_WORKING,
  EMPLOYMENT_STATUS_STUDENT_WORKING,
  GIC_ACCEPTED_COUNTRY_OPTION_LIST,
  GIC_FUND_SOURCE_LIST,
  MAX_COUNTRY_COUNT,
  MAX_FILE_SIZE_FOR_VALIDATION,
  MAX_FILE_SIZE_IN_MB_HELPTEXTNUMBER,
  MIN_COUNTRY_COUNT,
  MONTH_OPTION_LIST,
  OCCUPATIONS_LIST,
  OTHER_OCCUPATION,
  POSITIONS_LIST,
  PREFIX_LIST,
  PROVINCE_OPTION_LIST,
  RBC_GIC_FORM_PROPERTIES,
  SOURCE_OF_WEALTH_LIST,
  STUDENT_NOT_WORKING_LIST,
  STUDENT_WORKING_LIST,
  SUPPORTED_FILE_TYPES,
  SUPPORTED_FILE_TYPES_STR,
} from './constants';
import {
  aptOrSuiteSchema,
  canadianTaxProvinceSchema,
  citySchema,
  companyNameSchema,
  companyPhoneNumberSchema,
  confirmEmailAddressSchema,
  countrySchema,
  dateOfBirthSchema,
  emailAddressSchema,
  employmentStatusSchema,
  expectedArrivalDateSchema,
  expectedGraduationMonthSchema,
  expectedGraduationYearSchema,
  expiryDateSchema,
  gicFundSourceOtherSchema,
  gicFundSourceSchema,
  givenNameSchema,
  hasPoliticallyImportantPositionsSchema,
  isCanadianResidentForTaxSchema,
  isOtherCountryResidentForTaxSchema,
  isThirdPartyInvestmentSchema,
  issuingPlaceOrAuthoritySchema,
  levelOfStudyNotWorkingSchema,
  levelOfStudyWorkingSchema,
  numberAndStreetNameSchema,
  occupationOtherSchema,
  occupationSchema,
  otherCountryResidentForTaxCountry1Schema,
  otherCountryResidentForTaxCountry2Schema,
  otherCountryResidentForTaxCountry3Schema,
  passportNumberSchema,
  phoneNumberSchema,
  politicallyImportantFullNameSchema,
  politicallyImportantRelationshipSchema,
  politicallyImportantRoleSchema,
  postalCodeSchema,
  prefixSchema,
  provinceOfStudySchema,
  purposeSchema,
  rbcGicV2FormSchema,
  schoolSchema,
  sourceOfWealthOtherSchema,
  sourceOfWealthSchema,
  stateOrProvinceSchema,
  studentNameSchema,
  surnameSchema,
} from './formSchemas';
import { RbcGicEvents, RbcGicFormTypeV2 } from './types';

interface ApplicationFormProps {
  goNext: () => void;
  goBack: () => void;
  studentId?: string;
  formState: RbcGicFormTypeV2;
  setFormState: React.Dispatch<React.SetStateAction<RbcGicFormTypeV2>>;
  isStudentDetailsError?: boolean;
  isCurrentUserApplyingForThemselves?: boolean;
  validateValuesOnLoad?: boolean;
  isVasRbcGicExtendedCountriesEnabled?: boolean;
}

const { Field, Form, useFieldValues, useSetFieldValues, useTriggerValidation } =
  createForm<RbcGicFormTypeV2>();

const generateCheckboxOptions = (options: string[]) => (
  <>
    {options.map(option => (
      <CheckboxGroup.Option key={option} label={option} value={option} />
    ))}
  </>
);

function GridItemWithEmptyContent({
  columnStart,
  columnEnd,
}: {
  columnStart: number;
  columnEnd: number;
}) {
  return (
    <GridAlpha.Item columnStart={columnStart} columnEnd={columnEnd}>
      {null}
    </GridAlpha.Item>
  );
}

function EmploymentStatusDropdown({ validateOnLoad }: { validateOnLoad: boolean }) {
  const setFormValues = useSetFieldValues();
  const { t } = useTranslation();
  const toasts = useToasts();

  return (
    <Field
      appearance="native"
      as={Select}
      name={RBC_GIC_FORM_PROPERTIES.employmentStatus.propertyName}
      label={t('rbcv2.fieldLabels.employmentStatus')}
      required={validateOnLoad ? false : t('rbcv2.errors.employmentStatusRequired')}
      onChange={() => {
        setFormValues({
          companyName: '',
          companyPhoneNumber: '',
          occupation: '',
          levelOfStudy: '',
        });
      }}
      validate={value => {
        return zodValidateField(
          employmentStatusSchema(t),
          RBC_GIC_FORM_PROPERTIES.employmentStatus.propertyName,
          value,
          toasts,
          !!validateOnLoad,
        );
      }}
    >
      {EMPLOYMENT_STATUS_LIST.map(opt => (
        <Select.Option {...opt} key={opt.value} />
      ))}
    </Field>
  );
}

function LevelOfStudyDropdown({ validateOnLoad }: { validateOnLoad: boolean }) {
  const { employmentStatus, levelOfStudy } = useFieldValues(['employmentStatus', 'levelOfStudy']);
  const setFieldValues = useSetFieldValues();
  const { t } = useTranslation();
  const toasts = useToasts();
  let levelOfStudySchema = levelOfStudyNotWorkingSchema(t);
  let levelOfStudyList = STUDENT_NOT_WORKING_LIST;

  if (employmentStatus === EMPLOYMENT_STATUS_STUDENT_WORKING) {
    levelOfStudySchema = levelOfStudyWorkingSchema(t);
    levelOfStudyList = STUDENT_WORKING_LIST;
  }

  if (
    [EMPLOYMENT_STATUS_STUDENT_NOT_WORKING, EMPLOYMENT_STATUS_STUDENT_WORKING].includes(
      employmentStatus,
    )
  ) {
    return (
      <Field
        appearance="native"
        as={Select}
        name={RBC_GIC_FORM_PROPERTIES.levelOfStudy.propertyName}
        label={t('rbcv2.fieldLabels.levelOfStudy')}
        required={
          validateOnLoad
            ? false
            : t('rbcv2.errors.fieldRequired', {
                fieldName: t('rbcv2.fieldLabels.levelOfStudy'),
              })
        }
        validate={value => {
          return zodValidateField(
            levelOfStudySchema,
            RBC_GIC_FORM_PROPERTIES.levelOfStudy.propertyName,
            value,
            toasts,
            !!validateOnLoad,
          );
        }}
      >
        {levelOfStudyList.map(opt => (
          <Select.Option {...opt} key={opt.value} />
        ))}
      </Field>
    );
  } else {
    if (levelOfStudy) {
      setFieldValues({ levelOfStudy: '' });
    }
    return <></>;
  }
}

function generateOccupationOptions(occupations: string[]): JSX.Element[] {
  return occupations
    .sort()
    .map(occupation => <Combobox.Option label={occupation} value={occupation} key={occupation} />);
}

function OccupationCombobox({ validateOnLoad }: { validateOnLoad: boolean }) {
  const { employmentStatus, occupation } = useFieldValues(['employmentStatus', 'occupation']);
  const setFieldValues = useSetFieldValues();
  const { t } = useTranslation();
  const toasts = useToasts();
  if (
    employmentStatus === EMPLOYMENT_STATUS_STUDENT_WORKING ||
    employmentStatus === EMPLOYMENT_STATUS_EMPLOYED
  ) {
    return (
      <Field
        as={Combobox}
        name={RBC_GIC_FORM_PROPERTIES.occupation.propertyName}
        label={t('rbcv2.fieldLabels.occupation')}
        size="md"
        required={
          validateOnLoad
            ? false
            : t('rbcv2.errors.fieldRequired', {
                fieldName: t('rbcv2.fieldLabels.occupation'),
              })
        }
        filterOption={(option: ComboboxOptionProps, query: string) => {
          if (option.value.toLowerCase().includes(query.toLowerCase())) return true;
          return false;
        }}
        validate={value => {
          return zodValidateField(
            occupationSchema(t),
            RBC_GIC_FORM_PROPERTIES.occupation.propertyName,
            value,
            toasts,
            !!validateOnLoad,
          );
        }}
      >
        {generateOccupationOptions(OCCUPATIONS_LIST)}
      </Field>
    );
  } else {
    if (occupation) {
      setFieldValues({ occupation: '' });
    }
    return <></>;
  }
}

function CompanyNameInput({ validateOnLoad }: { validateOnLoad: boolean }) {
  const { employmentStatus, companyName } = useFieldValues(['employmentStatus', 'companyName']);
  const setFieldValues = useSetFieldValues();
  const { t } = useTranslation();
  const toasts = useToasts();
  if ([EMPLOYMENT_STATUS_STUDENT_WORKING, EMPLOYMENT_STATUS_EMPLOYED].includes(employmentStatus)) {
    return (
      <Field
        as={TextInput}
        name={RBC_GIC_FORM_PROPERTIES.companyName.propertyName}
        label={t('rbcv2.fieldLabels.companyName')}
        required={
          validateOnLoad
            ? false
            : t('rbcv2.errors.fieldRequired', {
                fieldName: t('rbcv2.fieldLabels.companyName'),
              })
        }
        validate={value => {
          return zodValidateField(
            companyNameSchema(t),
            RBC_GIC_FORM_PROPERTIES.companyName.propertyName,
            value,
            toasts,
            !!validateOnLoad,
          );
        }}
      ></Field>
    );
  } else {
    if (companyName) {
      setFieldValues({ companyName: '' });
    }

    return <></>;
  }
}

function SameAsResidentialAddressCheckbox() {
  const setFormValues = useSetFieldValues();
  const {
    aptOrSuiteResidential,
    numberAndStreetNameResidential,
    cityResidential,
    stateOrProvinceResidential,
    countryResidential,
    postalCodeResidential,
    isSameAsResidentialAddress,
  } = useFieldValues([
    'aptOrSuiteResidential',
    'numberAndStreetNameResidential',
    'cityResidential',
    'stateOrProvinceResidential',
    'countryResidential',
    'postalCodeResidential',
    'isSameAsResidentialAddress',
  ]);

  const setValues = () => {
    setFormValues({
      aptOrSuiteMailing: aptOrSuiteResidential,
      numberAndStreetNameMailing: numberAndStreetNameResidential,
      cityMailing: cityResidential,
      stateOrProvinceMailing: stateOrProvinceResidential,
      countryMailing: countryResidential,
      postalCodeMailing: postalCodeResidential,
    });
  };

  if (isSameAsResidentialAddress) {
    setValues();
  }

  return (
    <Field
      as={Checkbox}
      name={'isSameAsResidentialAddress' as const}
      label="Same as Residential Address"
      labelPlacement="right"
      onChange={value => {
        if (value) {
          setValues();
        }
      }}
    />
  );
}

function CanadianTaxProvinceDropdown({ validateOnLoad }: { validateOnLoad: boolean }) {
  const { isCanadianResidentForTax, canadianTaxProvince } = useFieldValues([
    'isCanadianResidentForTax',
    'canadianTaxProvince',
  ]);
  const setFormValues = useSetFieldValues();

  const { t } = useTranslation();
  const toasts = useToasts();
  if (isCanadianResidentForTax === 'Yes') {
    return (
      <>
        <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
        <GridAlpha.Item
          columnStart={{
            xs: 2,
            md: 2,
          }}
          columnEnd={{
            xs: 13,
            md: 8,
          }}
        >
          <Field
            as={Select}
            name={RBC_GIC_FORM_PROPERTIES.canadianTaxProvince.propertyName}
            label={t('rbcv2.fieldLabels.canadianTaxProvince')}
            required={validateOnLoad ? false : t('rbcv2.errors.canadianTaxProvinceRequired')}
            appearance="native"
            validate={value => {
              return zodValidateField(
                canadianTaxProvinceSchema(t),
                RBC_GIC_FORM_PROPERTIES.canadianTaxProvince.propertyName,
                value,
                toasts,
                !!validateOnLoad,
              );
            }}
          >
            {PROVINCE_OPTION_LIST.map(opt => (
              <Select.Option {...opt} key={opt.value} />
            ))}
          </Field>
        </GridAlpha.Item>
      </>
    );
  } else {
    if (canadianTaxProvince) {
      setFormValues({ canadianTaxProvince: '' });
    }

    return <></>;
  }
}

const COUNTRY_FIELD_DETAILS = [
  {
    name: RBC_GIC_FORM_PROPERTIES.otherCountryResidentForTaxCountry1.propertyName,
    label: (t: TFunction) => t('rbcv2.fieldLabels.otherCountryResidentForTaxCountry1'),
    fieldName: (t: TFunction) => t('rbcv2.fieldLabels.otherCountryResidentForTaxCountry1'),
    schema: (t: TFunction) => otherCountryResidentForTaxCountry1Schema(t),
  },
  {
    name: RBC_GIC_FORM_PROPERTIES.otherCountryResidentForTaxCountry2.propertyName,
    label: (t: TFunction) => t('rbcv2.fieldLabels.otherCountryResidentForTaxCountry2'),
    fieldName: (t: TFunction) => t('rbcv2.fieldLabels.otherCountryResidentForTaxCountry2'),
    schema: (t: TFunction) => otherCountryResidentForTaxCountry2Schema(t),
  },
  {
    name: RBC_GIC_FORM_PROPERTIES.otherCountryResidentForTaxCountry3.propertyName,
    label: (t: TFunction) => t('rbcv2.fieldLabels.otherCountryResidentForTaxCountry3'),
    fieldName: (t: TFunction) => t('rbcv2.fieldLabels.otherCountryResidentForTaxCountry3'),
    schema: (t: TFunction) => otherCountryResidentForTaxCountry3Schema(t),
  },
];

function OtherCountryResidentForTaxDropdowns({ validateOnLoad }: { validateOnLoad: boolean }) {
  const {
    isOtherCountryResidentForTax,
    numOfOtherCountryResidentForTax,
    otherCountryResidentForTaxCountry1,
    otherCountryResidentForTaxCountry2,
    otherCountryResidentForTaxCountry3,
  } = useFieldValues([
    'isOtherCountryResidentForTax',
    'numOfOtherCountryResidentForTax',
    'otherCountryResidentForTaxCountry1',
    'otherCountryResidentForTaxCountry2',
    'otherCountryResidentForTaxCountry3',
  ]);
  const setFieldValues = useSetFieldValues();
  const { t } = useTranslation();
  const toasts = useToasts();
  // We're using the numOfOtherCountryResidentForTax to restore the state of the form when go back from the verfification page.
  // This tracks the number of other tax countries that the user has selected previously.
  const currentCountryCount = Math.max(numOfOtherCountryResidentForTax ?? 0, MIN_COUNTRY_COUNT);
  const countryCountArray = new Array(currentCountryCount).fill(numOfOtherCountryResidentForTax);
  const isMaxCountryCount = countryCountArray.length >= MAX_COUNTRY_COUNT;

  if (isOtherCountryResidentForTax === 'Yes') {
    return (
      <>
        <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
        {countryCountArray.map((_val, index) => (
          <GridAlpha.Item
            key={index}
            columnStart={{
              xs: 2,
              md: 2,
            }}
            columnEnd={{
              xs: 13,
              md: 8,
            }}
          >
            <Field
              appearance="native"
              as={Select}
              name={COUNTRY_FIELD_DETAILS[index].name}
              label={COUNTRY_FIELD_DETAILS[index].label(t)}
              required={
                validateOnLoad || index > 0
                  ? false
                  : t('rbcv2.errors.fieldRequired', {
                      fieldName: COUNTRY_FIELD_DETAILS[index].fieldName(t),
                    })
              }
              validate={value => {
                return zodValidateField(
                  COUNTRY_FIELD_DETAILS[index].schema(t),
                  COUNTRY_FIELD_DETAILS[index].name,
                  value,
                  toasts,
                  !!validateOnLoad,
                );
              }}
            >
              {FULL_COUNTRY_OPTIONS_LIST.map(opt => (
                <Select.Option {...opt} key={opt.value} />
              ))}
            </Field>
          </GridAlpha.Item>
        ))}
        {!isMaxCountryCount && (
          <GridAlpha.Item
            columnStart={{
              xs: 2,
              md: 2,
            }}
            columnEnd={{
              xs: 13,
              md: 8,
            }}
          >
            <Link
              href="javascript:void(0)"
              onClick={() => {
                setFieldValues({ numOfOtherCountryResidentForTax: currentCountryCount + 1 });
              }}
            >
              {t('rbcv2.fieldLabels.addAnotherCountry')}
            </Link>
          </GridAlpha.Item>
        )}
      </>
    );
  } else {
    if (
      currentCountryCount > MIN_COUNTRY_COUNT ||
      otherCountryResidentForTaxCountry1 ||
      otherCountryResidentForTaxCountry2 ||
      otherCountryResidentForTaxCountry3
    ) {
      setFieldValues({
        numOfOtherCountryResidentForTax: MIN_COUNTRY_COUNT,
        otherCountryResidentForTaxCountry1: '',
        otherCountryResidentForTaxCountry2: '',
        otherCountryResidentForTaxCountry3: '',
      });
    }

    return <></>;
  }
}

function PositionList() {
  return (
    <>
      {POSITIONS_LIST.map((position, index) => (
        <li key={index}>{position}</li>
      ))}
    </>
  );
}

function PoliticallyExposedInputs({ validateOnLoad }: { validateOnLoad: boolean }) {
  const {
    hasPoliticallyImportantPositions,
    politicallyImportantFullName,
    politicallyImportantRelationship,
    politicallyImportantRole,
    sourceOfWealth,
    gicFundSource,
  } = useFieldValues([
    'hasPoliticallyImportantPositions',
    'politicallyImportantFullName',
    'politicallyImportantRelationship',
    'politicallyImportantRole',
    'sourceOfWealth',
    'gicFundSource',
  ]);
  const setFieldValues = useSetFieldValues();
  const { t } = useTranslation();
  const toasts = useToasts();
  if (hasPoliticallyImportantPositions === 'Yes') {
    return (
      <>
        <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
        <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
        <GridAlpha.Item
          columnStart={{
            xs: 2,
            md: 2,
          }}
          columnEnd={{
            xs: 13,
            md: 5,
          }}
        >
          <Field
            as={TextInput}
            name={RBC_GIC_FORM_PROPERTIES.politicallyImportantFullName.propertyName}
            label={t('rbcv2.fieldLabels.politicallyImportantFullName')}
            required={
              validateOnLoad
                ? false
                : t('rbcv2.errors.fieldRequired', {
                    fieldName: t('rbcv2.fieldLabels.politicallyImportantFullName'),
                  })
            }
            validate={value => {
              return zodValidateField(
                politicallyImportantFullNameSchema(t),
                RBC_GIC_FORM_PROPERTIES.politicallyImportantFullName.propertyName,
                value,
                toasts,
                !!validateOnLoad,
              );
            }}
          ></Field>
        </GridAlpha.Item>
        <GridAlpha.Item
          columnStart={{
            xs: 2,
            md: 5,
          }}
          columnEnd={{
            xs: 13,
            md: 8,
          }}
        >
          <Field
            as={TextInput}
            name={RBC_GIC_FORM_PROPERTIES.politicallyImportantRelationship.propertyName}
            label={t('rbcv2.fieldLabels.politicallyImportantRelationship')}
            required={
              validateOnLoad
                ? false
                : t('rbcv2.errors.fieldRequired', {
                    fieldName: t('rbcv2.fieldLabels.politicallyImportantRelationship'),
                  })
            }
            validate={value => {
              return zodValidateField(
                politicallyImportantRelationshipSchema(t),
                RBC_GIC_FORM_PROPERTIES.politicallyImportantRelationship.propertyName,
                value,
                toasts,
                !!validateOnLoad,
              );
            }}
          ></Field>
        </GridAlpha.Item>

        <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
        <GridAlpha.Item
          columnStart={{
            xs: 2,
            md: 2,
          }}
          columnEnd={{
            xs: 13,
            md: 5,
          }}
        >
          <Field
            as={TextInput}
            name={RBC_GIC_FORM_PROPERTIES.politicallyImportantRole.propertyName}
            label={t('rbcv2.fieldLabels.politicallyImportantRole')}
            required={
              validateOnLoad
                ? false
                : htmlDecode(
                    t('rbcv2.errors.fieldRequired', {
                      fieldName: t('rbcv2.fieldLabels.politicallyImportantRole'),
                    }),
                  )
            }
            validate={value => {
              return zodValidateField(
                politicallyImportantRoleSchema(t),
                RBC_GIC_FORM_PROPERTIES.politicallyImportantRole.propertyName,
                value,
                toasts,
                !!validateOnLoad,
              );
            }}
          ></Field>
        </GridAlpha.Item>
        <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
        <GridAlpha.Item
          columnStart={{
            xs: 2,
            md: 2,
          }}
          columnEnd={{
            xs: 13,
            md: 8,
          }}
        >
          <Field
            as={CheckboxGroup}
            name={RBC_GIC_FORM_PROPERTIES.sourceOfWealth.propertyName}
            label={t('rbcv2.fieldLabels.sourceOfWealth')}
            required={validateOnLoad ? false : t('rbcv2.errors.sourceOfWealthRequired')}
            validate={value => {
              return zodValidateField(
                sourceOfWealthSchema(t),
                RBC_GIC_FORM_PROPERTIES.sourceOfWealth.propertyName,
                value,
                toasts,
                !!validateOnLoad,
              );
            }}
          >
            {generateCheckboxOptions(SOURCE_OF_WEALTH_LIST)}
          </Field>
          <SourceOfWealthOtherInput validateOnLoad={validateOnLoad} />
        </GridAlpha.Item>

        <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
        <GridAlpha.Item
          columnStart={{
            xs: 2,
            md: 2,
          }}
          columnEnd={{
            xs: 13,
            md: 8,
          }}
        >
          <Field
            as={CheckboxGroup}
            name={RBC_GIC_FORM_PROPERTIES.gicFundSource.propertyName}
            label={t('rbcv2.fieldLabels.gicFundSource')}
            required={validateOnLoad ? false : t('rbcv2.errors.gicFundSourceRequired')}
            validate={value => {
              return zodValidateField(
                gicFundSourceSchema(t),
                RBC_GIC_FORM_PROPERTIES.gicFundSource.propertyName,
                value,
                toasts,
                !!validateOnLoad,
              );
            }}
          >
            {generateCheckboxOptions(GIC_FUND_SOURCE_LIST)}
          </Field>
          <GicFundSourceOtherInput validateOnLoad={validateOnLoad} />
        </GridAlpha.Item>
      </>
    );
  } else {
    if (
      politicallyImportantFullName ||
      politicallyImportantRelationship ||
      politicallyImportantRole ||
      sourceOfWealth.length > 0 ||
      gicFundSource.length > 0
    )
      setFieldValues({
        politicallyImportantFullName: '',
        politicallyImportantRelationship: '',
        politicallyImportantRole: '',
        sourceOfWealth: [],
        gicFundSource: [],
      });
    return <></>;
  }
}

function SourceOfWealthOtherInput({ validateOnLoad }: { validateOnLoad: boolean }) {
  const { sourceOfWealth, sourceOfWealthOther } = useFieldValues([
    'sourceOfWealth',
    'sourceOfWealthOther',
  ]);
  const { t } = useTranslation();
  const toasts = useToasts();
  const setFieldValues = useSetFieldValues();

  if (sourceOfWealth.includes('Other')) {
    return (
      <Field
        as={TextInput}
        name={RBC_GIC_FORM_PROPERTIES.sourceOfWealthOther.propertyName}
        label={t('rbcv2.fieldLabels.sourceOfWealthOther')}
        required={
          validateOnLoad
            ? false
            : t('rbcv2.errors.fieldRequired', {
                fieldName: t('rbcv2.fieldLabels.sourceOfWealthOther'),
              })
        }
        validate={value => {
          return zodValidateField(
            sourceOfWealthOtherSchema(t),
            RBC_GIC_FORM_PROPERTIES.sourceOfWealthOther.propertyName,
            value,
            toasts,
            !!validateOnLoad,
          );
        }}
      ></Field>
    );
  }

  if (sourceOfWealthOther) {
    setFieldValues({ sourceOfWealthOther: '' });
  }

  return <></>;
}

function GicFundSourceOtherInput({ validateOnLoad }: { validateOnLoad: boolean }) {
  const { gicFundSource, gicFundSourceOther } = useFieldValues([
    'gicFundSource',
    'gicFundSourceOther',
  ]);
  const { t } = useTranslation();
  const toasts = useToasts();
  const setFieldValues = useSetFieldValues();

  if (gicFundSource.includes('Other')) {
    return (
      <Field
        as={TextInput}
        name={RBC_GIC_FORM_PROPERTIES.gicFundSourceOther.propertyName}
        label={t('rbcv2.fieldLabels.gicFundSourceOther')}
        required={
          validateOnLoad
            ? false
            : t('rbcv2.errors.fieldRequired', {
                fieldName: t('rbcv2.fieldLabels.gicFundSourceOther'),
              })
        }
        validate={value => {
          return zodValidateField(
            gicFundSourceOtherSchema(t),
            RBC_GIC_FORM_PROPERTIES.gicFundSourceOther.propertyName,
            value,
            toasts,
            !!validateOnLoad,
          );
        }}
      ></Field>
    );
  }

  if (gicFundSourceOther) {
    setFieldValues({ gicFundSourceOther: '' });
  }

  return <></>;
}
function ResidentialFields({
  validateOnLoad,
  filteredCountryOptions,
  countriesRequireProvinces,
}: {
  validateOnLoad: boolean;
  filteredCountryOptions: {
    label: string;
    value: string;
  }[];
  countriesRequireProvinces: string[];
}) {
  const { isSameAsResidentialAddress } = useFieldValues(['isSameAsResidentialAddress']);
  const checkProvinceRequired = CheckProvinceRequired({ countriesRequireProvinces });
  const { t } = useTranslation();
  const toasts = useToasts();
  return (
    <>
      <GridAlpha.Item
        columnStart={{
          xs: 2,
          md: 2,
        }}
        columnEnd={{
          xs: 13,
          md: 5,
        }}
      >
        <TextInputWithFilter
          Field={Field}
          fieldName={RBC_GIC_FORM_PROPERTIES.aptOrSuiteResidential.propertyName}
          fieldLabel={t('rbcv2.fieldLabels.aptOrSuite')}
          required={false}
          defaultRegexOverride={/^[a-zA-Z0-9 .-]*$/}
          zodSchema={aptOrSuiteSchema}
          skipValidationIfEmptyValue={!!validateOnLoad}
        />
      </GridAlpha.Item>
      <GridAlpha.Item
        columnStart={{
          xs: 2,
          md: 5,
        }}
        columnEnd={{
          xs: 13,
          md: 8,
        }}
      >
        <Field
          as={TextInput}
          name={RBC_GIC_FORM_PROPERTIES.numberAndStreetNameResidential.propertyName}
          label={t('rbcv2.fieldLabels.numberAndStreetName')}
          required={
            validateOnLoad
              ? false
              : t('rbcv2.errors.fieldRequired', {
                  fieldName: t('rbcv2.fieldLabels.numberAndStreetName'),
                })
          }
          onChange={() => {
            <SameAsResidentialAddressCheckbox />;
          }}
          validate={(value, { triggerValidation }) => {
            if (isSameAsResidentialAddress) triggerValidation('numberAndStreetNameMailing');
            return zodValidateField(
              numberAndStreetNameSchema(t),
              RBC_GIC_FORM_PROPERTIES.numberAndStreetNameResidential.propertyName,
              value,
              toasts,
              !!validateOnLoad,
            );
          }}
        ></Field>
      </GridAlpha.Item>

      <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
      <GridAlpha.Item
        columnStart={{
          xs: 2,
          md: 2,
        }}
        columnEnd={{
          xs: 13,
          md: 5,
        }}
      >
        <Field
          as={TextInput}
          name={RBC_GIC_FORM_PROPERTIES.cityResidential.propertyName}
          label={t('rbcv2.fieldLabels.city')}
          required={
            validateOnLoad
              ? false
              : t('rbcv2.errors.fieldRequired', {
                  fieldName: t('rbcv2.fieldLabels.city'),
                })
          }
          onChange={() => {
            <SameAsResidentialAddressCheckbox />;
          }}
          validate={(value, { triggerValidation }) => {
            if (isSameAsResidentialAddress) triggerValidation('cityMailing');
            return zodValidateField(
              citySchema(t),
              RBC_GIC_FORM_PROPERTIES.cityResidential.propertyName,
              value,
              toasts,
              !!validateOnLoad,
            );
          }}
        ></Field>
      </GridAlpha.Item>
      <GridAlpha.Item
        columnStart={{
          xs: 2,
          md: 5,
        }}
        columnEnd={{
          xs: 13,
          md: 8,
        }}
      >
        <Field
          as={TextInput}
          name={RBC_GIC_FORM_PROPERTIES.stateOrProvinceResidential.propertyName}
          label={t('rbcv2.fieldLabels.stateOrProvince')}
          required={false}
          onChange={() => {
            <SameAsResidentialAddressCheckbox />;
          }}
          validate={(value, { triggerValidation }) => {
            if (checkProvinceRequired) return t('rbcv2.errors.provinceRequired');
            if (isSameAsResidentialAddress) triggerValidation('stateOrProvinceMailing');
            return zodValidateField(
              stateOrProvinceSchema(t),
              RBC_GIC_FORM_PROPERTIES.stateOrProvinceResidential.propertyName,
              value,
              toasts,
              !!validateOnLoad,
            );
          }}
        ></Field>
      </GridAlpha.Item>

      <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
      <GridAlpha.Item
        columnStart={{
          xs: 2,
          md: 2,
        }}
        columnEnd={{
          xs: 13,
          md: 5,
        }}
      >
        <Field
          appearance="native"
          as={Select}
          name={RBC_GIC_FORM_PROPERTIES.countryResidential.propertyName}
          label={t('rbcv2.fieldLabels.country')}
          required={
            validateOnLoad
              ? false
              : t('rbcv2.errors.fieldRequired', {
                  fieldName: t('rbcv2.fieldLabels.country'),
                })
          }
          onChange={() => {
            <SameAsResidentialAddressCheckbox />;
          }}
          validate={(value, { triggerValidation }) => {
            if (checkProvinceRequired) triggerValidation('stateOrProvinceResidential');
            if (isSameAsResidentialAddress) triggerValidation('countryMailing');
            return zodValidateField(
              countrySchema(t),
              RBC_GIC_FORM_PROPERTIES.countryResidential.propertyName,
              value,
              toasts,
              !!validateOnLoad,
            );
          }}
        >
          {filteredCountryOptions.map(opt => (
            <Select.Option {...opt} key={opt.value} />
          ))}
        </Field>
      </GridAlpha.Item>
      <GridAlpha.Item
        columnStart={{
          xs: 2,
          md: 5,
        }}
        columnEnd={{
          xs: 13,
          md: 8,
        }}
      >
        <Field
          as={TextInput}
          name={RBC_GIC_FORM_PROPERTIES.postalCodeResidential.propertyName}
          label={t('rbcv2.fieldLabels.postalCode')}
          required={false}
          onChange={() => {
            <SameAsResidentialAddressCheckbox />;
          }}
          validate={(value, { triggerValidation }) => {
            if (isSameAsResidentialAddress) triggerValidation('postalCodeMailing');
            return zodValidateField(
              postalCodeSchema(t),
              RBC_GIC_FORM_PROPERTIES.postalCodeResidential.propertyName,
              value,
              toasts,
              !!validateOnLoad,
            );
          }}
        ></Field>
      </GridAlpha.Item>
    </>
  );
}
function MailingFields({
  validateOnLoad,
  filteredCountryOptions,
}: {
  validateOnLoad: boolean;
  filteredCountryOptions: {
    label: string;
    value: string;
  }[];
}) {
  const { isSameAsResidentialAddress } = useFieldValues(['isSameAsResidentialAddress']);
  const { t } = useTranslation();
  const toasts = useToasts();
  return (
    <>
      <GridAlpha.Item
        columnStart={{
          xs: 2,
          md: 2,
        }}
        columnEnd={{
          xs: 13,
          md: 5,
        }}
      >
        <TextInputWithFilter
          Field={Field}
          fieldName={RBC_GIC_FORM_PROPERTIES.aptOrSuiteMailing.propertyName}
          fieldLabel={t('rbcv2.fieldLabels.aptOrSuite')}
          disabled={isSameAsResidentialAddress}
          required={false}
          defaultRegexOverride={/^$|^[a-zA-Z0-9 .-]+$/g}
          zodSchema={aptOrSuiteSchema}
          skipValidationIfEmptyValue={!!validateOnLoad}
        />
      </GridAlpha.Item>
      <GridAlpha.Item
        columnStart={{
          xs: 2,
          md: 5,
        }}
        columnEnd={{
          xs: 13,
          md: 8,
        }}
      >
        <Field
          as={TextInput}
          name={RBC_GIC_FORM_PROPERTIES.numberAndStreetNameMailing.propertyName}
          label={t('rbcv2.fieldLabels.numberAndStreetName')}
          disabled={isSameAsResidentialAddress}
          required={
            validateOnLoad
              ? false
              : t('rbcv2.errors.fieldRequired', {
                  fieldName: t('rbcv2.fieldLabels.numberAndStreetName'),
                })
          }
          validate={value => {
            return zodValidateField(
              numberAndStreetNameSchema(t),
              RBC_GIC_FORM_PROPERTIES.numberAndStreetNameMailing.propertyName,
              value,
              toasts,
              !!validateOnLoad,
            );
          }}
        ></Field>
      </GridAlpha.Item>
      <GridAlpha.Item
        columnStart={{
          xs: 2,
          md: 2,
        }}
        columnEnd={{
          xs: 13,
          md: 5,
        }}
      >
        <Field
          as={TextInput}
          name={RBC_GIC_FORM_PROPERTIES.cityMailing.propertyName}
          label={t('rbcv2.fieldLabels.city')}
          disabled={isSameAsResidentialAddress}
          required={
            validateOnLoad
              ? false
              : t('rbcv2.errors.fieldRequired', {
                  fieldName: t('rbcv2.fieldLabels.city'),
                })
          }
          validate={value => {
            return zodValidateField(
              citySchema(t),
              RBC_GIC_FORM_PROPERTIES.cityMailing.propertyName,
              value,
              toasts,
              !!validateOnLoad,
            );
          }}
        ></Field>
      </GridAlpha.Item>
      <GridAlpha.Item
        columnStart={{
          xs: 2,
          md: 5,
        }}
        columnEnd={{
          xs: 13,
          md: 8,
        }}
      >
        <Field
          as={TextInput}
          name={RBC_GIC_FORM_PROPERTIES.stateOrProvinceMailing.propertyName}
          label={t('rbcv2.fieldLabels.stateOrProvince')}
          required={false}
          disabled={isSameAsResidentialAddress}
          validate={value => {
            return zodValidateField(
              stateOrProvinceSchema(t),
              RBC_GIC_FORM_PROPERTIES.stateOrProvinceMailing.propertyName,
              value,
              toasts,
              !!validateOnLoad,
            );
          }}
        ></Field>
      </GridAlpha.Item>

      <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
      <GridAlpha.Item
        columnStart={{
          xs: 2,
          md: 2,
        }}
        columnEnd={{
          xs: 13,
          md: 5,
        }}
      >
        <Field
          appearance="native"
          as={Select}
          name={RBC_GIC_FORM_PROPERTIES.countryMailing.propertyName}
          label={t('rbcv2.fieldLabels.country')}
          disabled={isSameAsResidentialAddress}
          required={
            validateOnLoad
              ? false
              : t('rbcv2.errors.fieldRequired', {
                  fieldName: t('rbcv2.fieldLabels.country'),
                })
          }
          validate={value => {
            return zodValidateField(
              countrySchema(t),
              RBC_GIC_FORM_PROPERTIES.countryMailing.propertyName,
              value,
              toasts,
              !!validateOnLoad,
            );
          }}
        >
          {filteredCountryOptions.map(opt => (
            <Select.Option {...opt} key={opt.value} />
          ))}
        </Field>
      </GridAlpha.Item>
      <GridAlpha.Item
        columnStart={{
          xs: 2,
          md: 5,
        }}
        columnEnd={{
          xs: 13,
          md: 8,
        }}
      >
        <Field
          as={TextInput}
          name={RBC_GIC_FORM_PROPERTIES.postalCodeMailing.propertyName}
          label={t('rbcv2.fieldLabels.postalCode')}
          disabled={isSameAsResidentialAddress}
          required={false}
          validate={value => {
            return zodValidateField(
              postalCodeSchema(t),
              RBC_GIC_FORM_PROPERTIES.postalCodeMailing.propertyName,
              value,
              toasts,
              !!validateOnLoad,
            );
          }}
        ></Field>
      </GridAlpha.Item>
    </>
  );
}

function OccupationField({ validateOnLoad }: { validateOnLoad: boolean }) {
  const { occupation, occupationOther } = useFieldValues(['occupation', 'occupationOther']);
  const setFieldValues = useSetFieldValues();

  const { t } = useTranslation();
  const toasts = useToasts();

  if (occupation === OTHER_OCCUPATION) {
    return (
      <>
        <GridAlpha.Item
          columnStart={{
            xs: 2,
            md: 2,
          }}
          columnEnd={{
            xs: 13,
            md: 5,
          }}
        >
          <OccupationCombobox validateOnLoad={validateOnLoad} />
        </GridAlpha.Item>
        <GridAlpha.Item
          columnStart={{
            xs: 2,
            md: 5,
          }}
          columnEnd={{
            xs: 13,
            md: 9,
          }}
        >
          <Field
            as={TextInput}
            name={RBC_GIC_FORM_PROPERTIES.occupationOther.propertyName}
            label={t('rbcv2.fieldLabels.occupationOther')}
            required={
              validateOnLoad
                ? false
                : t('rbcv2.errors.fieldRequired', {
                    fieldName: t('rbcv2.fieldLabels.occupationOther'),
                  })
            }
            validate={value => {
              return zodValidateField(
                occupationOtherSchema(t),
                RBC_GIC_FORM_PROPERTIES.occupationOther.propertyName,
                value,
                toasts,
                !!validateOnLoad,
              );
            }}
          ></Field>
        </GridAlpha.Item>
      </>
    );
  }

  if (occupationOther) {
    setFieldValues({ occupationOther: '' });
  }

  return (
    <GridAlpha.Item
      columnStart={{
        xs: 2,
        md: 2,
      }}
      columnEnd={{
        xs: 13,
        md: 5,
      }}
    >
      <OccupationCombobox validateOnLoad={validateOnLoad} />
    </GridAlpha.Item>
  );
}

function CheckProvinceRequired({
  countriesRequireProvinces,
}: {
  countriesRequireProvinces: string[];
}): boolean {
  const { countryResidential, stateOrProvinceResidential } = useFieldValues([
    'countryResidential',
    'stateOrProvinceResidential',
  ]);
  return countriesRequireProvinces.includes(countryResidential) && !stateOrProvinceResidential;
}

function CompanyPhoneNumberInput({ validateOnLoad }: { validateOnLoad: boolean }) {
  const { employmentStatus, companyPhoneNumber } = useFieldValues([
    'employmentStatus',
    'companyPhoneNumber',
  ]);
  const setFieldValues = useSetFieldValues();
  const { t } = useTranslation();
  const toasts = useToasts();

  if (
    employmentStatus === EMPLOYMENT_STATUS_STUDENT_WORKING ||
    employmentStatus === EMPLOYMENT_STATUS_EMPLOYED
  ) {
    return (
      <Field
        as={PhoneInput}
        name={RBC_GIC_FORM_PROPERTIES.companyPhoneNumber.propertyName}
        label={t('rbcv2.fieldLabels.companyPhoneNumber')}
        required={validateOnLoad ? false : t('rbcv2.errors.invalidCompanyPhoneNumber')}
        phoneNumberFormat="international"
        validate={value => {
          if (validateOnLoad && !value) {
            return true;
          }
          const result = zodValidateField(
            companyPhoneNumberSchema(t),
            RBC_GIC_FORM_PROPERTIES.companyPhoneNumber.propertyName,
            value,
            toasts,
          );
          if (result === true) {
            const parsedPhoneNumber = parsePhoneNumberFromString(value);
            if (!parsedPhoneNumber?.isPossible() || !parsedPhoneNumber?.isValid()) {
              return t('rbcv2.errors.invalidPhoneNumber');
            }
          }
          return result;
        }}
      ></Field>
    );
  } else {
    if (companyPhoneNumber) {
      setFieldValues({ companyPhoneNumber: '' });
    }

    return <></>;
  }
}

function OnLoadValidateOnlyNonEmptyFields({
  validateOnLoad,
  setValidateOnLoad,
}: {
  validateOnLoad: boolean;
  setValidateOnLoad: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const triggerValidation = useTriggerValidation();
  if (validateOnLoad) {
    triggerValidation().then(() => setValidateOnLoad(false));
  }
  return <></>;
}

const ApplicationForm = ({
  studentId = '',
  formState,
  setFormState,
  goNext,
  isStudentDetailsError = false,
  isCurrentUserApplyingForThemselves = false,
  validateValuesOnLoad = false,
  isVasRbcGicExtendedCountriesEnabled = false,
}: ApplicationFormProps) => {
  const { t } = useTranslation();
  const formId = useId();
  const toasts = useToasts();
  const { rpId, utmSource, source } = useGetTrackingEventData();
  const { isCreatingRbcGicApplication, createRbcGicApplication } =
    useCreateRbcGicApplicationV2(rpId);
  const [submitBtnDisabled, setSubmitBtnDisabled] = useState(false);
  const [validateOnLoad, setValidateOnLoad] = useState(validateValuesOnLoad);
  const { trackEvent } = useEventTracker();

  const valuesToExclude = ['VN', 'CN', 'PH'];

  const filteredCountryOptions = isVasRbcGicExtendedCountriesEnabled
    ? GIC_ACCEPTED_COUNTRY_OPTION_LIST
    : GIC_ACCEPTED_COUNTRY_OPTION_LIST.filter(country => !valuesToExclude.includes(country.value));

  const countriesRequireProvinces = isVasRbcGicExtendedCountriesEnabled
    ? COUNTRIES_REQUIRE_PROVINCES
    : COUNTRIES_REQUIRE_PROVINCES.filter(country => !valuesToExclude.includes(country));

  useEffect(() => {
    trackEvent({
      event: RbcGicEvents.PageView,
      data: { rp_id: rpId, utm_source: utmSource, source: source },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isStudentDetailsError) {
    if (isCurrentUserApplyingForThemselves) {
      toasts.negative(new Error(t('landingPage.errorMessages.unableToFetchYourDetails')));
    } else {
      toasts.negative(new Error(t('landingPage.errorMessages.unableToFetchStudentDetails')));
    }
  }

  /**
   * These variables are solely used for fields where we need to cross check validation between itself and other fields.
   *     Example: Either first name or surname is required
   * Existing implementation of crystal-ui's form library does not support triggering validation of
   * other fields without causing an infinite loop.
   */
  let isSurnameAlreadyValidating = false;
  let isGivenNameAlreadyValidating = false;
  let isCanadianResidentForTaxAlreadyValidating = false;
  let isOtherCountryResidentForTaxAlreadyValidating = false;
  let isExpectedGraduationMonthAlreadyValidating = false;
  let isExpectedGraduationYearAlreadyValidating = false;

  return (
    <div>
      <div
        style={{
          backgroundColor: '#005CB2',
          width: '100%',
          height: '240px',
          //TODO: This flexbox div's flexbox messes with grid alpha's width... not sure what to do
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        <GridAlpha columns={COLUMN_SETTING}>
          <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
          <GridAlpha.Item
            align="center"
            columnStart={{
              xs: 2,
              md: 2,
            }}
            columnEnd={{
              xs: 4,
              md: 4,
            }}
          >
            <RbcLogo />
          </GridAlpha.Item>

          <GridAlpha.Item
            columnStart={{
              xs: 4,
              md: 4,
            }}
            columnEnd={{
              xs: 12,
              md: 12,
            }}
            align="center"
          >
            <Text intent="secondary" variant="titleL" contrast="inverted" align="start">
              {t('rbcv2.formTitle')}
            </Text>
          </GridAlpha.Item>
          <GridItemWithEmptyContent columnStart={12} columnEnd={14} />
        </GridAlpha>
      </div>

      <GridAlpha columns={COLUMN_SETTING} pt={6} pb={6}>
        <GridAlpha.Item
          align="center"
          columnStart={{
            xs: 2,
            md: 2,
          }}
          columnEnd={{
            xs: 13,
            md: 13,
          }}
        >
          <div
            style={{
              backgroundColor: '#F6F8FE',
              width: '100%',
              height: 'auto',
              display: 'flex',
              justifyContent: 'start',
              alignItems: 'center',
            }}
          >
            <Text intent="secondary" variant="labelM" align="start">
              {t('rbcv2.formDescription')}
            </Text>
          </div>
        </GridAlpha.Item>
      </GridAlpha>

      <div
        style={{
          backgroundColor: '#FFFFFF',
          width: '100%',
          display: 'flex',
          justifyContent: 'start',
          alignItems: 'center',
        }}
      >
        <Form
          id={formId}
          defaultValues={formState}
          onSubmit={async formValues => {
            setSubmitBtnDisabled(true);

            const queryParamRpId = rpId?.trim() as unknown;
            if (
              queryParamRpId &&
              (!(typeof queryParamRpId === 'string' || queryParamRpId instanceof String) ||
                queryParamRpId.length > 256)
            ) {
              // Checks if query param is invalid ahead of time so user gets clear feedback instead of generic error
              toasts.negative(new Error(t('rbc.errors.invalidRpId')));
              setSubmitBtnDisabled(false);
              return;
            }

            let createBody: CreateRbcGicApplicationReqV2Body;
            try {
              const parsedFormValues = rbcGicV2FormSchema(t).parse(formValues);
              //@ts-expect-error : TODO fix this
              const mappedValues: RbcGicFormTypeV2 = mapValues(parsedFormValues, val =>
                val === '' ? undefined : val,
              ); // Map empty strings to undefineds
              mappedValues.studentId = studentId ? studentId : undefined;
              createBody = await formDataToCreateV2Body(
                mappedValues,
                (queryParamRpId as string) || undefined,
                source?.trim() || utmSource?.trim() || undefined,
              );
            } catch (e) {
              console.log('Failed to submit', e);

              const error = new Error(`RBC GIC Submission Validation failure. Reason ${e}`);
              trackEvent({
                event: RbcGicEvents.StartedFormSubmissionValidationErrored,
                data: { rp_id: rpId, utm_source: utmSource, source: source },
                error,
              });

              if (e instanceof ZodError) {
                toasts.negative(new Error(t('common.errors.invalidSubmission')));
                setSubmitBtnDisabled(false);
                return;
              }
              console.error('Unexpected error', e);
              toasts.negative(e as Error);
              setSubmitBtnDisabled(false);
              return;
            }

            createRbcGicApplication(
              createBody,
              (response: CreateRbcGicApplicationResV2Body) => {
                toasts.positive(t('rbcv2.applicationSubmitted'));
                formValues.id = response.data.id;
                setFormState(formValues);
                trackEvent({
                  event: RbcGicEvents.StartedForm,
                  data: { rp_id: rpId, utm_source: utmSource, source: source },
                });
                goNext();
              },
              error => {
                console.error('error: ', error);
                const toastError = error as Error;
                let errorMessage = toastError.message ?? t('rbcv2.errors.gicCreationFailure');
                if (error instanceof VasError) {
                  const vasError: VasError = error;
                  const errorBody: VasErrorBody = vasError.errorBody;
                  errorMessage = vasError.message;
                  if (vasError.errorBody.statusCode === 409) {
                    errorMessage =
                      errorBody.message instanceof Array<string>
                        ? errorBody.message.join(',')
                        : errorBody.message;
                  }
                }

                const e = new Error(`RBC GIC Submission API failure. Reason ${errorMessage}`);

                trackEvent({
                  event: RbcGicEvents.StartedFormSubmissionApiErrored,
                  data: { rp_id: rpId, utm_source: utmSource, source: source },
                  error: e,
                });
                toasts.negative(new Error(errorMessage));
                setSubmitBtnDisabled(false);
              },
            );
          }}
        >
          <GridAlpha columns={COLUMN_SETTING} columnGap={4} rowGap={0} pt={10}>
            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 12,
                md: 12,
              }}
            >
              <Field
                as={FileUpload}
                allowedFileTypes={SUPPORTED_FILE_TYPES}
                helpText={t('rbcv2.fileHelpText', {
                  supportedFileTypes: SUPPORTED_FILE_TYPES_STR(),
                  maxFileSizeInMB: MAX_FILE_SIZE_IN_MB_HELPTEXTNUMBER,
                })}
                label={t('rbcv2.fieldLabels.letterOfAcceptanceFile')}
                name={RBC_GIC_FORM_PROPERTIES.letterOfAcceptanceFile.propertyName}
                maxFileSize={MAX_FILE_SIZE_FOR_VALIDATION}
                size="sm"
                required={validateOnLoad ? false : t('rbcv2.errors.letterOfAcceptanceFileRequired')}
                uploadFile={async (
                  file: File,
                  {
                    onError,
                    onSuccess,
                  }: {
                    onSuccess: (fileId: string) => void;
                    onError: (errorMessage?: string) => void;
                  },
                ) => {
                  const isValidFile = await validateSupportedMimeTypes(file);
                  if (!isValidFile) {
                    onError(t('common.errors.imageDataInvalid'));
                    return;
                  }
                  onSuccess(file.name);
                }}
              >
                <FileUpload.Button>{t('common.browseFiles')}</FileUpload.Button>
              </Field>
            </GridAlpha.Item>
            <GridItemWithEmptyContent columnStart={12} columnEnd={14} />
            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
          </GridAlpha>
          <GridAlpha
            columns={COLUMN_SETTING}
            columnGap={4}
            rowGap={6}
            rows={{
              xs: 'auto',
              md: 'repeat(3, auto)',
            }}
            pt={14}
            pb={10}
          >
            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 12,
                md: 12,
              }}
            >
              <Text intent="secondary" variant="labelM">
                {t('rbcv2.passportHelpText')}
              </Text>
            </GridAlpha.Item>
            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 3,
                md: 3,
              }}
            >
              <Field
                appearance="native"
                as={Select}
                name={RBC_GIC_FORM_PROPERTIES.prefix.propertyName}
                label={t('rbcv2.fieldLabels.prefix')}
                validate={value => {
                  return zodValidateField(
                    prefixSchema(t),
                    RBC_GIC_FORM_PROPERTIES.prefix.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              >
                {PREFIX_LIST.map(opt => (
                  <Select.Option {...opt} key={opt.value} />
                ))}
              </Field>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 5,
              }}
            >
              <Field
                as={TextInput}
                name={RBC_GIC_FORM_PROPERTIES.givenName.propertyName}
                label={t('rbcv2.fieldLabels.givenName')}
                required={false}
                validate={(value, { values, triggerValidation }) => {
                  if (isGivenNameAlreadyValidating) {
                    return true;
                  }

                  const givenNameValidationResult = zodValidateField(
                    givenNameSchema(t),
                    RBC_GIC_FORM_PROPERTIES.givenName.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );

                  if (givenNameValidationResult === true) {
                    isGivenNameAlreadyValidating = true;
                    if (!isSurnameAlreadyValidating) {
                      triggerValidation('surname');
                    }
                    isGivenNameAlreadyValidating = false;

                    return zodValidateField(
                      studentNameSchema(t),
                      RBC_GIC_FORM_PROPERTIES.givenName.propertyName,
                      {
                        givenName: value,
                        surname: values.surname,
                      },
                      toasts,
                      !!validateOnLoad,
                    );
                  }

                  return givenNameValidationResult;
                }}
              />
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 5,
              }}
              columnEnd={{
                xs: 13,
                md: 8,
              }}
            >
              <Field
                as={TextInput}
                name={RBC_GIC_FORM_PROPERTIES.surname.propertyName}
                label={t('rbcv2.fieldLabels.surname')}
                required={false}
                validate={(value, { values, triggerValidation }) => {
                  if (isSurnameAlreadyValidating) {
                    return true;
                  }

                  const surnameValidationResult = zodValidateField(
                    surnameSchema(t),
                    RBC_GIC_FORM_PROPERTIES.surname.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );

                  if (surnameValidationResult === true) {
                    isSurnameAlreadyValidating = true;
                    if (!isGivenNameAlreadyValidating) {
                      triggerValidation('givenName');
                    }
                    isSurnameAlreadyValidating = false;
                    return zodValidateField(
                      studentNameSchema(t),
                      RBC_GIC_FORM_PROPERTIES.surname.propertyName,
                      {
                        givenName: values.givenName,
                        surname: value,
                      },
                      toasts,
                      !!validateOnLoad,
                    );
                  }

                  return surnameValidationResult;
                }}
              ></Field>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 5,
              }}
            >
              <Field
                as={DatePicker}
                name={RBC_GIC_FORM_PROPERTIES.dateOfBirth.propertyName}
                label={t('rbcv2.fieldLabels.dateOfBirth')}
                required={
                  validateOnLoad
                    ? false
                    : t('rbcv2.errors.fieldRequired', {
                        fieldName: t('rbcv2.fieldLabels.dateOfBirth'),
                      })
                }
                granularity="day"
                minDate="1900-01-01T00:00:00.000Z"
                maxDate={startOfDay(new Date()).toISOString()}
                size="md"
                validate={value => {
                  return zodValidateField(
                    dateOfBirthSchema(t),
                    RBC_GIC_FORM_PROPERTIES.dateOfBirth.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              ></Field>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Field
                as={RadioGroup}
                name={RBC_GIC_FORM_PROPERTIES.purpose.propertyName}
                label={t('rbcv2.fieldLabels.purpose')}
                required={validateOnLoad ? false : t('rbcv2.errors.purposeRequired')}
                validate={value => {
                  return zodValidateField(
                    purposeSchema(t),
                    RBC_GIC_FORM_PROPERTIES.purpose.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              >
                <RadioGroup.Option label="Living Expenses" value="Living expenses" />
                <RadioGroup.Option label="School/Tuition" value="School/tuition" />
                <RadioGroup.Option label="Investment/Savings" value="Investment/savings" />
              </Field>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Text intent="secondary" variant="titleL">
                {t('rbcv2.sectionHeadings.occupation')}
              </Text>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 5,
              }}
            >
              <EmploymentStatusDropdown validateOnLoad={validateOnLoad} />
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 7,
              }}
            >
              <LevelOfStudyDropdown validateOnLoad={validateOnLoad} />
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <OccupationField validateOnLoad={validateOnLoad} />

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 5,
              }}
            >
              <CompanyNameInput validateOnLoad={validateOnLoad} />
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 5,
              }}
              columnEnd={{
                xs: 13,
                md: 9,
              }}
            >
              <CompanyPhoneNumberInput validateOnLoad={validateOnLoad} />
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Text intent="secondary" variant="titleL">
                {t('rbcv2.sectionHeadings.contact')}
              </Text>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 8,
              }}
            >
              <Notice actionText="" intent="secondary" width="fill">
                {t('rbcv2.emailVerificationHelpText')}
              </Notice>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 6,
              }}
            >
              <Field
                as={PhoneInput}
                name={RBC_GIC_FORM_PROPERTIES.phoneNumber.propertyName}
                label={t('rbcv2.fieldLabels.phoneNumber')}
                required={validateOnLoad ? false : t('rbcv2.errors.invalidPhoneNumber')}
                phoneNumberFormat="international"
                validate={value => {
                  if (validateOnLoad && !value) {
                    return true;
                  }
                  const result = zodValidateField(
                    phoneNumberSchema(t),
                    RBC_GIC_FORM_PROPERTIES.phoneNumber.propertyName,
                    value,
                    toasts,
                  );
                  if (result === true) {
                    const parsedPhoneNumber = parsePhoneNumberFromString(value);
                    if (!parsedPhoneNumber?.isPossible() || !parsedPhoneNumber?.isValid()) {
                      return t('rbcv2.errors.invalidPhoneNumber');
                    }
                  }
                  return result;
                }}
              ></Field>
              <Field
                as={Checkbox}
                name={RBC_GIC_FORM_PROPERTIES.isMobileNumber.propertyName}
                label={t('rbcv2.fieldLabels.isMobileNumber')}
                labelPlacement="right"
              />
            </GridAlpha.Item>

            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 6,
              }}
              columnEnd={{
                xs: 13,
                md: 9,
              }}
            >
              <Field
                as={TextInput}
                name={RBC_GIC_FORM_PROPERTIES.emailAddress.propertyName}
                label={t('rbcv2.fieldLabels.emailAddress')}
                required={
                  validateOnLoad
                    ? false
                    : t('rbcv2.errors.fieldRequired', {
                        fieldName: t('rbcv2.fieldLabels.emailAddress'),
                      })
                }
                validate={value => {
                  return zodValidateField(
                    emailAddressSchema(t),
                    RBC_GIC_FORM_PROPERTIES.emailAddress.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              ></Field>
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 9,
              }}
              columnEnd={{
                xs: 13,
                md: 12,
              }}
            >
              <Field
                as={TextInput}
                name={RBC_GIC_FORM_PROPERTIES.confirmEmailAddress.propertyName}
                label={t('rbcv2.fieldLabels.confirmEmailAddress')}
                required={
                  validateOnLoad
                    ? false
                    : t('rbcv2.errors.fieldRequired', {
                        fieldName: t('rbcv2.fieldLabels.confirmEmailAddress'),
                      })
                }
                validate={(value, { values }) => {
                  if (validateOnLoad && !value) {
                    return true;
                  }
                  if (value.trim() !== values.emailAddress.trim()) {
                    return t('rbcv2.errors.emailShouldMatch', {
                      confirmEmailFieldName: t('rbcv2.fieldLabels.confirmEmailAddress'),
                      emailFieldName: t('rbcv2.fieldLabels.emailAddress'),
                    });
                  }
                  return zodValidateField(
                    confirmEmailAddressSchema(t),
                    RBC_GIC_FORM_PROPERTIES.confirmEmailAddress.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              ></Field>
            </GridAlpha.Item>
            <GridItemWithEmptyContent columnStart={12} columnEnd={14} />

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Text intent="secondary" variant="titleL">
                {t('rbcv2.sectionHeadings.residential')}
              </Text>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <ResidentialFields
              validateOnLoad={validateOnLoad}
              filteredCountryOptions={filteredCountryOptions}
              countriesRequireProvinces={countriesRequireProvinces}
            />

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Text intent="secondary" variant="titleL">
                {t('rbcv2.sectionHeadings.mailing')}
              </Text>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <SameAsResidentialAddressCheckbox />
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <MailingFields
              validateOnLoad={validateOnLoad}
              filteredCountryOptions={filteredCountryOptions}
            />

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Text intent="secondary" variant="titleL">
                {t('rbcv2.sectionHeadings.passport')}
              </Text>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 5,
              }}
            >
              <Field
                as={TextInput}
                name={RBC_GIC_FORM_PROPERTIES.passportNumber.propertyName}
                label={t('rbcv2.fieldLabels.passportNumber')}
                required={
                  validateOnLoad
                    ? false
                    : t('rbcv2.errors.fieldRequired', {
                        fieldName: t('rbcv2.fieldLabels.passportNumber'),
                      })
                }
                validate={value => {
                  return zodValidateField(
                    passportNumberSchema(t),
                    RBC_GIC_FORM_PROPERTIES.passportNumber.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              ></Field>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 5,
              }}
            >
              <Field
                appearance="native"
                as={Select}
                name={RBC_GIC_FORM_PROPERTIES.issuingCountry.propertyName}
                label={t('rbcv2.fieldLabels.issuingCountry')}
                required={
                  validateOnLoad
                    ? false
                    : t('rbcv2.errors.fieldRequired', {
                        fieldName: t('rbcv2.fieldLabels.issuingCountry'),
                      })
                }
                validate={value => {
                  return zodValidateField(
                    countrySchema(t),
                    RBC_GIC_FORM_PROPERTIES.issuingCountry.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              >
                {filteredCountryOptions.map(opt => (
                  <Select.Option {...opt} key={opt.value} />
                ))}
              </Field>
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 5,
              }}
              columnEnd={{
                xs: 13,
                md: 8,
              }}
            >
              <Field
                as={TextInput}
                name={RBC_GIC_FORM_PROPERTIES.issuingPlaceOrAuthority.propertyName}
                label={t('rbcv2.fieldLabels.issuingPlaceOrAuthority')}
                required={
                  validateOnLoad
                    ? false
                    : t('rbcv2.errors.fieldRequired', {
                        fieldName: t('rbcv2.fieldLabels.issuingPlaceOrAuthority'),
                      })
                }
                validate={value => {
                  return zodValidateField(
                    issuingPlaceOrAuthoritySchema(t),
                    RBC_GIC_FORM_PROPERTIES.issuingPlaceOrAuthority.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              />
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 5,
              }}
            >
              <Field
                as={DatePicker}
                name={RBC_GIC_FORM_PROPERTIES.expiryDate.propertyName}
                label={t('rbcv2.fieldLabels.expiryDate')}
                required={
                  validateOnLoad
                    ? false
                    : t('rbcv2.errors.fieldRequired', {
                        fieldName: t('rbcv2.fieldLabels.expiryDate'),
                      })
                }
                granularity="day"
                minDate={startOfDay(addMonths(new Date(), 1)).toISOString()}
                maxDate={startOfDay(addYears(new Date(), 20)).toISOString()}
                size="md"
                validate={value => {
                  return zodValidateField(
                    expiryDateSchema(t),
                    RBC_GIC_FORM_PROPERTIES.expiryDate.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              ></Field>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Text intent="secondary" variant="titleL">
                {t('rbcv2.sectionHeadings.schoolLocation')}
              </Text>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 5,
              }}
            >
              <TextInputWithFilter
                Field={Field}
                fieldName={RBC_GIC_FORM_PROPERTIES.school.propertyName}
                fieldLabel={t('rbcv2.fieldLabels.school')}
                required={true}
                defaultRegexOverride={/^[a-zA-Z0-9 .'-]*$/}
                zodSchema={schoolSchema}
                skipValidationIfEmptyValue={!!validateOnLoad}
              />
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 5,
              }}
              columnEnd={{
                xs: 13,
                md: 8,
              }}
            >
              <Field
                appearance="native"
                as={Select}
                name={RBC_GIC_FORM_PROPERTIES.provinceOfStudy.propertyName}
                label={t('rbcv2.fieldLabels.provinceOfStudy')}
                required={
                  validateOnLoad
                    ? false
                    : t('rbcv2.errors.fieldRequired', {
                        fieldName: t('rbcv2.fieldLabels.provinceOfStudy'),
                      })
                }
                validate={value => {
                  return zodValidateField(
                    provinceOfStudySchema(t),
                    RBC_GIC_FORM_PROPERTIES.provinceOfStudy.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              >
                {PROVINCE_OPTION_LIST.map(opt => (
                  <Select.Option {...opt} key={opt.value} />
                ))}
              </Field>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 5,
              }}
            >
              <Field
                as={DatePicker}
                name={RBC_GIC_FORM_PROPERTIES.expectedArrivalDate.propertyName}
                label={t('rbcv2.fieldLabels.expectedArrivalDate')}
                required={
                  validateOnLoad
                    ? false
                    : t('rbcv2.errors.fieldRequired', {
                        fieldName: t('rbcv2.fieldLabels.expectedArrivalDate'),
                      })
                }
                granularity="day"
                minDate={startOfDay(addDays(new Date(), 7)).toISOString()}
                maxDate={startOfDay(addMonths(new Date(), 18)).toISOString()}
                size="md"
                validate={value => {
                  return zodValidateField(
                    expectedArrivalDateSchema(t),
                    RBC_GIC_FORM_PROPERTIES.expectedArrivalDate.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              ></Field>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Text intent="secondary" variant="titleS">
                {t('rbcv2.graduationDateHelpText')}
              </Text>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 5,
              }}
            >
              <Field
                appearance="native"
                as={Select}
                name={RBC_GIC_FORM_PROPERTIES.expectedGraduationMonth.propertyName}
                label={t('rbcv2.fieldLabels.expectedGraduationMonth')}
                required={
                  validateOnLoad
                    ? false
                    : t('rbcv2.errors.fieldRequired', {
                        fieldName: t('rbcv2.fieldLabels.expectedGraduationMonth'),
                      })
                }
                validate={(value, { values, triggerValidation }) => {
                  if (validateOnLoad && !value) {
                    return true;
                  }
                  if (isExpectedGraduationMonthAlreadyValidating) {
                    return true;
                  } else {
                    isExpectedGraduationMonthAlreadyValidating = true;
                  }
                  if (!isExpectedGraduationYearAlreadyValidating) {
                    triggerValidation('expectedGraduationYear');
                  }
                  isExpectedGraduationMonthAlreadyValidating = false;

                  const currentMonth = new Date().getMonth() + 1;
                  const currentYear = new Date().getFullYear();
                  const selectedMonth = parseInt(value, 10);
                  const selectedYear = parseInt(values.expectedGraduationYear, 10);

                  if (
                    selectedYear < currentYear ||
                    (selectedYear === currentYear && selectedMonth <= currentMonth)
                  ) {
                    return t('rbcv2.errors.graduationDateMustBeFuture');
                  }

                  return zodValidateField(
                    expectedGraduationMonthSchema(t),
                    RBC_GIC_FORM_PROPERTIES.expectedGraduationMonth.propertyName,
                    value,
                    toasts,
                  );
                }}
              >
                {MONTH_OPTION_LIST.map(opt => (
                  <Select.Option {...opt} key={opt.value} />
                ))}
              </Field>
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 5,
              }}
              columnEnd={{
                xs: 13,
                md: 8,
              }}
            >
              <Field
                as={NumberInput}
                name={RBC_GIC_FORM_PROPERTIES.expectedGraduationYear.propertyName}
                label={t('rbcv2.fieldLabels.expectedGraduationYear')}
                required={
                  validateOnLoad
                    ? false
                    : t('rbcv2.errors.fieldRequired', {
                        fieldName: t('rbcv2.fieldLabels.expectedGraduationYear'),
                      })
                }
                validate={(value, { values, triggerValidation }) => {
                  if (validateOnLoad && !value) {
                    return true;
                  }
                  if (isExpectedGraduationYearAlreadyValidating) {
                    return true;
                  } else {
                    isExpectedGraduationYearAlreadyValidating = true;
                  }
                  if (!isExpectedGraduationMonthAlreadyValidating) {
                    triggerValidation('expectedGraduationMonth');
                  }
                  isExpectedGraduationYearAlreadyValidating = false;

                  const currentDate = new Date();
                  const sixMonthsAheadCurrentDate = new Date();
                  sixMonthsAheadCurrentDate.setMonth(currentDate.getMonth() + 6);
                  const expectedGraduationDate = new Date(
                    Number(values.expectedGraduationYear),
                    Number(values.expectedGraduationMonth) - 1,
                  );

                  if (expectedGraduationDate < sixMonthsAheadCurrentDate) {
                    return t('rbcv2.errors.graduationDateAtLeastSixMonthsAhead');
                  }

                  const currentMonth = new Date().getMonth() + 1;
                  const currentYear = new Date().getFullYear();
                  const selectedMonth = parseInt(values.expectedGraduationMonth, 10);
                  const selectedYear = parseInt(value, 10);
                  if (
                    selectedYear < currentYear ||
                    (selectedYear === currentYear && selectedMonth <= currentMonth)
                  ) {
                    return t('rbcv2.errors.graduationDateMustBeFuture');
                  }

                  return zodValidateField(
                    expectedGraduationYearSchema(t),
                    RBC_GIC_FORM_PROPERTIES.expectedGraduationYear.propertyName,
                    value,
                    toasts,
                  );
                }}
              />
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Flex gap={2}>
                <Text intent="secondary" variant="titleL">
                  {t('rbcv2.sectionHeadings.thirdParty')}
                </Text>
                <Tooltip label={t('rbcv2.thirdPartyTooltip')} placement="top">
                  <Tooltip.Icon emphasis="transparent" intent="secondary" />
                </Tooltip>
              </Flex>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Field
                as={RadioGroup}
                name={RBC_GIC_FORM_PROPERTIES.isThirdPartyInvestment.propertyName}
                label={t('rbcv2.fieldLabels.isThirdPartyInvestment')}
                required={validateOnLoad ? false : t('rbcv2.errors.isThirdPartyInvestmentRequired')}
                validate={value => {
                  if (value === 'Yes') {
                    return t('rbcv2.errors.mustNotBeThirdParty');
                  }
                  return zodValidateField(
                    isThirdPartyInvestmentSchema(t),
                    RBC_GIC_FORM_PROPERTIES.isThirdPartyInvestment.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              >
                <RadioGroup.Option label="Yes" value="Yes" />
                <RadioGroup.Option label="No" value="No" />
              </Field>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Text intent="secondary" variant="titleL">
                {t('rbcv2.sectionHeadings.taxStatus')}
              </Text>
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 8,
              }}
            >
              <Text intent="secondary" variant="titleS">
                {t('rbcv2.taxHeadingNoticeText')}
              </Text>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Field
                as={RadioGroup}
                name={RBC_GIC_FORM_PROPERTIES.isCanadianResidentForTax.propertyName}
                label={t('rbcv2.fieldLabels.isCanadianResidentForTax')}
                required={
                  validateOnLoad ? false : t('rbcv2.errors.isCanadianResidentForTaxRequired')
                }
                validate={(value, { values, triggerValidation }) => {
                  if (isCanadianResidentForTaxAlreadyValidating) {
                    return true;
                  } else {
                    isCanadianResidentForTaxAlreadyValidating = true;
                  }
                  if (
                    !isOtherCountryResidentForTaxAlreadyValidating &&
                    values.isOtherCountryResidentForTax
                  ) {
                    triggerValidation('isOtherCountryResidentForTax');
                  }
                  isCanadianResidentForTaxAlreadyValidating = false;

                  if (value === 'No' && values.isOtherCountryResidentForTax === 'No') {
                    return t('rbcv2.errors.eitherCanadianOrOther');
                  }

                  return zodValidateField(
                    isCanadianResidentForTaxSchema(t),
                    RBC_GIC_FORM_PROPERTIES.isCanadianResidentForTax.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              >
                <RadioGroup.Option label="Yes" value="Yes" />
                <RadioGroup.Option label="No" value="No" />
              </Field>
            </GridAlpha.Item>
            <CanadianTaxProvinceDropdown validateOnLoad={validateOnLoad} />

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 8,
              }}
            >
              <Field
                as={RadioGroup}
                name={RBC_GIC_FORM_PROPERTIES.isOtherCountryResidentForTax.propertyName}
                label={t('rbcv2.fieldLabels.isOtherCountryResidentForTax')}
                required={
                  validateOnLoad ? false : t('rbcv2.errors.isOtherCountryResidentForTaxRequired')
                }
                validate={(value, { values, triggerValidation }) => {
                  if (isOtherCountryResidentForTaxAlreadyValidating) {
                    return true;
                  } else {
                    isOtherCountryResidentForTaxAlreadyValidating = true;
                  }
                  if (
                    !isCanadianResidentForTaxAlreadyValidating &&
                    values.isCanadianResidentForTax
                  ) {
                    triggerValidation('isCanadianResidentForTax');
                  }
                  isOtherCountryResidentForTaxAlreadyValidating = false;

                  if (value === 'No' && values.isCanadianResidentForTax === 'No') {
                    return t('rbcv2.errors.eitherCanadianOrOther');
                  }

                  return zodValidateField(
                    isOtherCountryResidentForTaxSchema(t),
                    RBC_GIC_FORM_PROPERTIES.isOtherCountryResidentForTax.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              >
                <Text intent="secondary" variant="labelS" contrast="low">
                  {t('rbcv2.taxResidentHelpText')}
                </Text>
                <RadioGroup.Option label="Yes" value="Yes" />
                <RadioGroup.Option label="No" value="No" />
              </Field>
            </GridAlpha.Item>
            <OtherCountryResidentForTaxDropdowns validateOnLoad={validateOnLoad} />
            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 13,
              }}
            >
              <Text intent="secondary" variant="titleL">
                {t('rbcv2.sectionHeadings.politicallyExposed')}
              </Text>
            </GridAlpha.Item>

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 8,
              }}
            >
              <Field
                as={RadioGroup}
                name={RBC_GIC_FORM_PROPERTIES.hasPoliticallyImportantPositions.propertyName}
                label={t('rbcv2.fieldLabels.hasPoliticallyImportantPositions')}
                required={
                  validateOnLoad
                    ? false
                    : t('rbcv2.errors.hasPoliticallyImportantPositionsRequired')
                }
                validate={value => {
                  return zodValidateField(
                    hasPoliticallyImportantPositionsSchema(t),
                    RBC_GIC_FORM_PROPERTIES.hasPoliticallyImportantPositions.propertyName,
                    value,
                    toasts,
                    !!validateOnLoad,
                  );
                }}
              >
                <Text intent="secondary" variant="labelS" contrast="low">
                  <ul>
                    <PositionList />
                  </ul>
                </Text>
                <Flex gap={8}>
                  <RadioGroup.Option label="Yes" value="Yes" />
                  <RadioGroup.Option label="No" value="No" />
                </Flex>
              </Field>
            </GridAlpha.Item>

            <PoliticallyExposedInputs validateOnLoad={validateOnLoad} />

            <GridItemWithEmptyContent columnStart={1} columnEnd={2} />
            <GridAlpha.Item
              columnStart={{
                xs: 2,
                md: 2,
              }}
              columnEnd={{
                xs: 13,
                md: 8,
              }}
            >
              <Button
                intent="primary"
                size="lg"
                width="hug"
                type="submit"
                form={formId}
                disabled={submitBtnDisabled}
                loading={isCreatingRbcGicApplication}
              >
                {t('rbcv2.continueGicApplicationButtonText')}
              </Button>
            </GridAlpha.Item>
          </GridAlpha>
          {/* OnLoadValidateOnlyNonEmptyFields needs to be called after all fields all set, so the last component on the form */}
          <OnLoadValidateOnlyNonEmptyFields
            validateOnLoad={validateOnLoad}
            setValidateOnLoad={setValidateOnLoad}
          />
        </Form>
      </div>
    </div>
  );
};

export default ApplicationForm;
